import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { RoundView, SideView, YouView } from "../../services/generated/client.js";
import type { TakenSlot } from "../../components/game/Timeline.js";
import { GuessClaim } from "../../components/game/GuessClaim.js";
import { OptionGrid } from "../../components/game/OptionGrid.js";
import { Timeline } from "../../components/game/Timeline.js";

/**
 * Contest the active side's placement — **with an answer of your own**.
 *
 * Phase 20h-1, ADR 0029. A challenge used to be one tap on a gap. It is now the
 * same thing the active player did: pick the slot, name the artist and the
 * title, add a year if you want one.
 *
 * # 🔴 The slot is the only part that wins the card
 *
 * That is not a UI detail, it is what keeps the engine tiebreak-free: exactly
 * one slot is correct and at most one player may hold any given slot, so at most
 * one challenger can take the card. Artist, title and year pay **coins**,
 * because coins are divisible and a card is not — and they pay nothing at all
 * unless the slot was right. The copy says so before the grids, because the
 * whole of finding #14 was that nobody could tell what a challenge contested.
 *
 * # The slot comes first, and the rest only after it
 *
 * Modelled on [`PlacePanel`], and for the reason recorded there: a second step
 * that appears the moment the phase opens competes with the decision the player
 * is actually making. Here it is stronger — the gap is the *only* thing that
 * decides whether any of the rest is worth anything, so asking for it first is
 * asking the question in the order it is answered.
 */
export function ChallengePanel({
  round,
  activeSide,
  activeLabel,
  claim,
  claimedSlot,
  takenSlots,
  you,
  price,
  busy,
  onChallenge,
}: {
  round: RoundView;
  /** The side being contested — the timeline the claim is drawn on. */
  activeSide: SideView | null;
  /** Their name, for the "claimed by" marker. */
  activeLabel: string | null;
  /** The gap they took. Claiming it is agreeing, not challenging. */
  claimedSlot: number | null;
  /** Gaps other challengers already hold. */
  takenSlots: readonly TakenSlot[];
  /** What the active side said it was — `null` until the grids close. */
  claim: { artist: string | null; title: string | null } | null;
  /** The viewer, for the options `narrow_options` struck out. */
  you: YouView | null;
  /** What it costs, said before the decision. */
  price: number;
  busy: boolean;
  onChallenge: (
    slot: number,
    artistOption: number | null,
    titleOption: number | null,
    exactYear: number | null,
  ) => void;
}) {
  const { t } = useTranslation();
  const [slot, setSlot] = useState<number | null>(null);
  const [artist, setArtist] = useState<number | null>(null);
  const [title, setTitle] = useState<number | null>(null);
  const [exactYear, setExactYear] = useState("");

  return (
    <>
      <p className="text-sm text-white/70">{t("round.challenge.cost", { price })}</p>
      {/* ⚠️ **The sentence finding #14 was missing, rewritten for 20h-1.** It
          used to say a challenge contests where the card goes "not who it is
          by", which stopped being true the moment a challenger could answer. */}
      <p className="text-xs text-white/50">{t("round.challenge.slotWinsCard")}</p>

      {claim && <GuessClaim artist={claim.artist} title={claim.title} />}

      <Timeline
        label={t("round.challenge.timeline")}
        cards={activeSide?.timeline ?? []}
        hidden={activeSide?.timeline_hidden ?? false}
        claimedSlot={claimedSlot}
        claimedBy={activeLabel}
        takenSlots={takenSlots}
        selectedSlot={slot}
        onPlace={(chosen) => {
          setSlot(chosen);
        }}
      />

      {slot !== null && (
        <div
          data-testid="challenge-answer-step"
          className="flex flex-col gap-3 rounded-xl border-2 border-flash-500/40 bg-flash-500/5 p-3"
        >
          <p className="text-xs text-flash-400">{t("round.challenge.answerBonus")}</p>

          <OptionGrid
            label={t("tally.artist")}
            options={round.artist_options}
            selected={artist}
            removed={you?.removed_artist_options ?? []}
            onSelect={(index) => {
              setArtist(artist === index ? null : index);
            }}
          />
          <OptionGrid
            label={t("tally.title")}
            options={round.title_options}
            selected={title}
            removed={you?.removed_title_options ?? []}
            onSelect={(index) => {
              setTitle(title === index ? null : index);
            }}
          />

          <label className="text-sm font-semibold" htmlFor="challenge-year">
            {t("round.exactYear")}
          </label>
          <input
            id="challenge-year"
            inputMode="numeric"
            placeholder="1984"
            value={exactYear}
            onChange={(event) => {
              setExactYear(event.target.value.replace(/\D/g, "").slice(0, 4));
            }}
            className="min-h-14 rounded-lg border border-white/15 bg-stage-900 px-3 text-2xl tabular-nums"
          />

          <button
            type="button"
            disabled={busy}
            onClick={() => {
              onChallenge(
                slot,
                artist,
                title,
                exactYear.length === 4 ? Number(exactYear) : null,
              );
            }}
            className="min-h-14 rounded-lg bg-flash-500 text-lg font-semibold text-stage-950 disabled:opacity-40"
          >
            {t("round.challenge.confirm", { price })}
          </button>
        </div>
      )}
    </>
  );
}
